// Event configurations for all meet types
import { getSettings, ALL_EVENTS, getEventDefaultsForLevel } from './settings';

// Helper function to get event defaults from settings based on event name and level
function getEventSettingsDefaults(eventName, level) {
  // Normalize level: 'middle-school' -> 'middle', 'high-school' -> 'high'
  const normalizedLevel = level.replace('-school', '').replace('professional', 'college');
  
  // Try to find matching event in ALL_EVENTS
  const eventNameLower = eventName.toLowerCase();
  
  for (const event of ALL_EVENTS) {
    const settingsEventName = event.name.toLowerCase();
    const eventId = event.id;
    
    // Match by name or ID
    if (eventNameLower.includes(settingsEventName) || 
        settingsEventName.includes(eventNameLower.split(' ')[0]) ||
        eventNameLower.includes(eventId.replace(/_/g, ' ')) ||
        eventNameLower.includes(eventId.replace(/_/g, ''))) {
      
      const levelDefaults = event.levels?.[normalizedLevel];
      if (levelDefaults) {
        return { heats: levelDefaults.heats, duration: levelDefaults.duration };
      }
    }
  }
  
  // Try matching by distance (e.g., "100m" in "100m Dash Prelims")
  const distanceMatch = eventNameLower.match(/(\d+)m/);
  if (distanceMatch) {
    const distance = distanceMatch[1];
    for (const event of ALL_EVENTS) {
      if (event.id.startsWith(distance + 'm') || event.id === distance + 'm') {
        const levelDefaults = event.levels?.[normalizedLevel];
        if (levelDefaults) {
          return { heats: levelDefaults.heats, duration: levelDefaults.duration };
        }
      }
    }
  }
  
  return null;
}

export const EVENT_CONFIGURATIONS = {
  'college-outdoor': [
    { name: '4x800m Relay', type: 'relay', eventId: '4x800m' },
    { name: '100m Hurdles', type: 'hurdles', eventId: '100m_hurdles', womenOnly: true },
    { name: '110m Hurdles', type: 'hurdles', eventId: '110m_hurdles', menOnly: true },
    { name: '100m Dash Prelims', type: 'sprint', eventId: '100m' },
    { name: '4x200m Relay', type: 'relay', eventId: '4x200m' },
    { name: '1600m', type: 'distance', eventId: '1600m' },
    { name: '400m', type: 'sprint', eventId: '400m' },
    { name: '400m Hurdles Final', type: 'hurdles', eventId: '400m_hurdles' },
    { name: '800m', type: 'distance', eventId: '800m' },
    { name: '200m', type: 'sprint', eventId: '200m' },
    { name: '3000m Steeplechase', type: 'distance', eventId: '3000m_steeple' },
    { name: '3200m', type: 'distance', eventId: '3200m' },
    { name: '4x100m Relay', type: 'relay', eventId: '4x100m' },
    { name: '4x400m Relay', type: 'relay', eventId: '4x400m' },
  ],
  'high-school-outdoor': [
    { name: '4x800m Relay', type: 'relay', eventId: '4x800m' },
    { name: '100m Hurdles', type: 'hurdles', eventId: '100m_hurdles', girlsOnly: true },
    { name: '110m Hurdles', type: 'hurdles', eventId: '110m_hurdles', boysOnly: true },
    { name: '100m Dash', type: 'sprint', eventId: '100m' },
    { name: '4x200m Relay', type: 'relay', eventId: '4x200m' },
    { name: '1600m', type: 'distance', eventId: '1600m' },
    { name: '4x100m Relay', type: 'relay', eventId: '4x100m' },
    { name: '400m', type: 'sprint', eventId: '400m' },
    { name: '300m Hurdles', type: 'hurdles', eventId: '300m_hurdles' },
    { name: '800m', type: 'distance', eventId: '800m' },
    { name: '200m', type: 'sprint', eventId: '200m' },
    { name: '3200m', type: 'distance', eventId: '3200m' },
    { name: '4x400m Relay', type: 'relay', eventId: '4x400m' },
  ],
  'middle-school-outdoor': [
    { name: '4x800m Relay', type: 'relay', eventId: '4x800m' },
    { name: '100m Hurdles', type: 'hurdles', eventId: '100m_hurdles', girlsOnly: true },
    { name: '110m Hurdles', type: 'hurdles', eventId: '110m_hurdles', boysOnly: true },
    { name: '100m Dash', type: 'sprint', eventId: '100m' },
    { name: '4x200m Relay', type: 'relay', eventId: '4x200m' },
    { name: '1600m', type: 'distance', eventId: '1600m' },
    { name: '4x100m Relay', type: 'relay', eventId: '4x100m' },
    { name: '400m', type: 'sprint', eventId: '400m' },
    { name: '200m Hurdles', type: 'hurdles', eventId: '200m_hurdles' },
    { name: '800m', type: 'distance', eventId: '800m' },
    { name: '200m', type: 'sprint', eventId: '200m' },
    { name: '4x400m Relay', type: 'relay', eventId: '4x400m' },
  ],
  'high-school-indoor': [
    { name: '4x800m Relay', type: 'relay', eventId: '4x800m' },
    { name: '60m Hurdles Prelims', type: 'hurdles', eventId: '60m_hurdles' },
    { name: '60m Dash Prelims', type: 'sprint', eventId: '60m' },
    { name: '800m', type: 'distance', eventId: '800m' },
    { name: '200m', type: 'sprint', eventId: '200m' },
    { name: '60m Hurdles Final', type: 'hurdles', eventId: '60m_hurdles' },
    { name: '60m Dash Final', type: 'sprint', eventId: '60m' },
    { name: '400m', type: 'sprint', eventId: '400m' },
    { name: '1600m', type: 'distance', eventId: '1600m' },
    { name: '3200m', type: 'distance', eventId: '3200m' },
    { name: '4x200m Relay', type: 'relay', eventId: '4x200m' },
    { name: '4x400m Relay', type: 'relay', eventId: '4x400m' },
    { name: '4x1600m Relay', type: 'relay', eventId: '4x1600m' },
  ],
  'middle-school-indoor': [
    { name: '4x800m Relay', type: 'relay', eventId: '4x800m' },
    { name: '55m Hurdles Prelims', type: 'hurdles', eventId: '55m_hurdles' },
    { name: '55m Dash Prelims', type: 'sprint', eventId: '55m' },
    { name: '800m', type: 'distance', eventId: '800m' },
    { name: '200m', type: 'sprint', eventId: '200m' },
    { name: '55m Hurdles Final', type: 'hurdles', eventId: '55m_hurdles' },
    { name: '55m Dash Final', type: 'sprint', eventId: '55m' },
    { name: '400m', type: 'sprint', eventId: '400m' },
    { name: '1600m', type: 'distance', eventId: '1600m' },
    { name: '4x200m Relay', type: 'relay', eventId: '4x200m' },
    { name: '4x400m Relay', type: 'relay', eventId: '4x400m' },
  ],
  'college-indoor': [
    { name: '4x800m Relay', type: 'relay', eventId: '4x800m' },
    { name: '60m Hurdles Prelims', type: 'hurdles', eventId: '60m_hurdles' },
    { name: '60m Dash Prelims', type: 'sprint', eventId: '60m' },
    { name: '1600m', type: 'distance', eventId: '1600m' },
    { name: '400m', type: 'sprint', eventId: '400m' },
    { name: '60m Hurdles Final', type: 'hurdles', eventId: '60m_hurdles' },
    { name: '60m Dash Final', type: 'sprint', eventId: '60m' },
    { name: '800m', type: 'distance', eventId: '800m' },
    { name: '200m', type: 'sprint', eventId: '200m' },
    { name: '3000m', type: 'distance', eventId: '3000m' },
    { name: '5000m', type: 'distance', eventId: '5000m' },
    { name: '4x200m Relay', type: 'relay', eventId: '4x200m' },
    { name: '4x400m Relay', type: 'relay', eventId: '4x400m' },
    { name: '4x1600m Relay', type: 'relay', eventId: '4x1600m' },
  ],
};

export function getEventsForMeet(level, type) {
  const key = `${level}-${type}`;
  const baseEvents = EVENT_CONFIGURATIONS[key] || [];
  const settings = getSettings();
  
  // Normalize level for settings lookup
  const normalizedLevel = level.replace('-school', '').replace('professional', 'college');
  
  // Build events with level-specific defaults from settings
  let events = baseEvents.map(event => {
    let modifiedEvent = { ...event };
    
    // Get defaults from settings based on eventId or event name
    let defaults = null;
    if (event.eventId) {
      defaults = getEventDefaultsForLevel(event.eventId, level);
    }
    if (!defaults) {
      defaults = getEventSettingsDefaults(event.name, level);
    }
    
    // Apply defaults - duration and heats from settings
    if (defaults) {
      modifiedEvent.duration = defaults.duration;
      modifiedEvent.defaultHeats = defaults.heats;
      // For gender-specific durations, use the same duration by default
      modifiedEvent.durationGirls = defaults.duration;
      modifiedEvent.durationBoys = defaults.duration;
      modifiedEvent.durationWomen = defaults.duration;
      modifiedEvent.durationMen = defaults.duration;
    } else {
      // Fallback defaults if no settings match found
      modifiedEvent.duration = modifiedEvent.duration || '03:00';
      modifiedEvent.defaultHeats = modifiedEvent.defaultHeats || 3;
    }
    
    // Short Sprint Distance preference (55m vs 60m)
    if (event.name.includes('60m Dash') && settings.shortSprintDistance === '55m') {
      modifiedEvent.name = event.name.replace('60m Dash', '55m Dash');
      const newDefaults = getEventDefaultsForLevel('55m', level);
      if (newDefaults) {
        modifiedEvent.duration = newDefaults.duration;
        modifiedEvent.defaultHeats = newDefaults.heats;
      }
    } else if (event.name.includes('55m Dash') && settings.shortSprintDistance === '60m') {
      modifiedEvent.name = event.name.replace('55m Dash', '60m Dash');
      const newDefaults = getEventDefaultsForLevel('60m', level);
      if (newDefaults) {
        modifiedEvent.duration = newDefaults.duration;
        modifiedEvent.defaultHeats = newDefaults.heats;
      }
    }
    
    // Short Hurdles Distance preference (55m vs 60m)
    if (event.name.includes('60m Hurdles') && settings.shortHurdlesDistance === '55m') {
      modifiedEvent.name = event.name.replace('60m Hurdles', '55m Hurdles');
      const newDefaults = getEventDefaultsForLevel('55m_hurdles', level);
      if (newDefaults) {
        modifiedEvent.duration = newDefaults.duration;
        modifiedEvent.defaultHeats = newDefaults.heats;
      }
    } else if (event.name.includes('55m Hurdles') && settings.shortHurdlesDistance === '60m') {
      modifiedEvent.name = event.name.replace('55m Hurdles', '60m Hurdles');
      const newDefaults = getEventDefaultsForLevel('60m_hurdles', level);
      if (newDefaults) {
        modifiedEvent.duration = newDefaults.duration;
        modifiedEvent.defaultHeats = newDefaults.heats;
      }
    }
    
    // Middle Distance Run preference (1500m vs 1600m)
    if (event.name === '1600m' && settings.middleDistanceRun === '1500m') {
      modifiedEvent.name = '1500m';
      const newDefaults = getEventDefaultsForLevel('1500m', level);
      if (newDefaults) {
        modifiedEvent.duration = newDefaults.duration;
        modifiedEvent.defaultHeats = newDefaults.heats;
      }
    } else if (event.name === '1500m' && settings.middleDistanceRun === '1600m') {
      modifiedEvent.name = '1600m';
      const newDefaults = getEventDefaultsForLevel('1600m', level);
      if (newDefaults) {
        modifiedEvent.duration = newDefaults.duration;
        modifiedEvent.defaultHeats = newDefaults.heats;
      }
    }
    
    // Long Hurdles Distance preference (300m vs 400m)
    if (event.name.includes('300m Hurdles') && settings.longHurdlesDistance === '400m') {
      modifiedEvent.name = event.name.replace('300m Hurdles', '400m Hurdles');
      const newDefaults = getEventDefaultsForLevel('400m_hurdles', level);
      if (newDefaults) {
        modifiedEvent.duration = newDefaults.duration;
        modifiedEvent.defaultHeats = newDefaults.heats;
      }
    } else if (event.name.includes('400m Hurdles') && settings.longHurdlesDistance === '300m') {
      modifiedEvent.name = event.name.replace('400m Hurdles', '300m Hurdles');
      const newDefaults = getEventDefaultsForLevel('300m_hurdles', level);
      if (newDefaults) {
        modifiedEvent.duration = newDefaults.duration;
        modifiedEvent.defaultHeats = newDefaults.heats;
      }
    }
    
    return modifiedEvent;
  });
  
  // Add Optional Events if enabled in settings
  
  // Shuttle Hurdle Relay
  if (settings.shuttleHurdleRelay) {
    const hasShuttleHurdle = events.some(e => e.name.includes('Shuttle Hurdle'));
    if (!hasShuttleHurdle) {
      const defaults = getEventDefaultsForLevel('shuttle_hurdle', level);
      events.push({ 
        name: 'Shuttle Hurdle Relay', 
        type: 'relay',
        eventId: 'shuttle_hurdle',
        duration: defaults?.duration || '04:00',
        defaultHeats: defaults?.heats || 3 
      });
    }
  }
  
  // Sprint Medley Relay
  if (settings.sprintMedleyRelay) {
    const hasSprintMedley = events.some(e => e.name.includes('Sprint Medley'));
    if (!hasSprintMedley) {
      const defaults = getEventDefaultsForLevel('sprint_medley', level);
      events.push({ 
        name: 'Sprint Medley Relay', 
        type: 'relay',
        eventId: 'sprint_medley',
        duration: defaults?.duration || '06:00',
        defaultHeats: defaults?.heats || 3 
      });
    }
  }
  
  // Distance Medley Relay
  if (settings.distanceMedleyRelay) {
    const hasDistanceMedley = events.some(e => e.name.includes('Distance Medley'));
    if (!hasDistanceMedley) {
      const defaults = getEventDefaultsForLevel('distance_medley', level);
      events.push({ 
        name: 'Distance Medley Relay', 
        type: 'relay',
        eventId: 'distance_medley',
        duration: defaults?.duration || '16:30',
        defaultHeats: defaults?.heats || 1 
      });
    }
  }
  
  // 1500m Race Walk
  if (settings.raceWalk1500m) {
    const has1500RW = events.some(e => e.name.includes('1500m Race Walk'));
    if (!has1500RW) {
      const defaults = getEventDefaultsForLevel('1500m_racewalk', level);
      events.push({ 
        name: '1500m Race Walk', 
        type: 'distance',
        eventId: '1500m_racewalk',
        duration: defaults?.duration || '12:30',
        defaultHeats: defaults?.heats || 1 
      });
    }
  }
  
  // 3000m Race Walk
  if (settings.raceWalk3000m) {
    const has3000RW = events.some(e => e.name.includes('3000m Race Walk'));
    if (!has3000RW) {
      const defaults = getEventDefaultsForLevel('3000m_racewalk', level);
      events.push({ 
        name: '3000m Race Walk', 
        type: 'distance',
        eventId: '3000m_racewalk',
        duration: defaults?.duration || '25:00',
        defaultHeats: defaults?.heats || 1 
      });
    }
  }
  
  // 5000m Race Walk
  if (settings.raceWalk5000m) {
    const has5000RW = events.some(e => e.name.includes('5000m Race Walk'));
    if (!has5000RW) {
      const defaults = getEventDefaultsForLevel('5000m_racewalk', level);
      events.push({ 
        name: '5000m Race Walk', 
        type: 'distance',
        eventId: '5000m_racewalk',
        duration: defaults?.duration || '40:00',
        defaultHeats: defaults?.heats || 1 
      });
    }
  }
  
  return events;
}

export function getGenderLabel(level) {
  if (level === 'college' || level === 'professional') {
    return { female: 'Women', male: 'Men' };
  }
  return { female: 'Girls', male: 'Boys' };
}

export function parseDuration(duration) {
  const [min, sec] = duration.split(':').map(Number);
  return min * 60 + sec;
}

export function formatDuration(seconds) {
  const min = Math.floor(seconds / 60);
  const sec = seconds % 60;
  return `${min}:${sec.toString().padStart(2, '0')}`;
}
